import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, FileText, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAccountAuth } from '@/app/hooks/useAccountAuth';
import { accountBillingApi } from '@/services/endpoints/account/billing';
import { accountOrganizationApi } from '@/services/endpoints/account/organization';
import { ACCOUNT_BILLING_KEYS, ACCOUNT_ORG_KEYS } from '@/components/account/queryKeys';
import {
  DataRow,
  EmptyState,
  LoadingRows,
  Panel,
  SectionTitle,
  StatusPill,
  SUBSCRIPTION_LABEL,
  SUBSCRIPTION_TONE,
  formatDay,
  formatMinorMoney,
  formatMoney,
} from '@/components/account/accountUi';

export default function AccountOverview() {
  const { isOwner, displayName, context } = useAccountAuth();

  const { data: dashboard, isLoading } = useQuery({
    queryKey: ACCOUNT_BILLING_KEYS.dashboard,
    queryFn: accountBillingApi.getDashboard,
    enabled: isOwner,
    staleTime: 60_000,
  });

  const { data: invoices } = useQuery({
    queryKey: ACCOUNT_BILLING_KEYS.invoices,
    queryFn: accountBillingApi.getInvoices,
    enabled: isOwner,
    staleTime: 60_000,
  });

  const { data: members } = useQuery({
    queryKey: ACCOUNT_ORG_KEYS.membersSummary,
    queryFn: () => accountOrganizationApi.getMembers({ limit: 1 }),
    staleTime: 5 * 60_000,
  });

  const subscription = dashboard?.subscription ?? null;
  const currency = subscription?.currency ?? 'cad';
  const basePlan = subscription?.items?.find((item) => item.planType === 'base');
  const addons = subscription?.items?.filter((item) => item.planType === 'addon') ?? [];
  const recentInvoices = (invoices ?? []).slice(0, 3);

  const firstName = displayName.split(' ')[0];

  return (
    <>
      <SectionTitle
        title={`Welcome back, ${firstName}`}
        description={`Everything billed to ${context?.organization?.name ?? 'your organization'} lives here.`}
      />

      <div className="space-y-6">
        {isOwner && !isLoading && !subscription && (
          <Panel>
            <EmptyState
              icon={Sparkles}
              title="No active subscription"
              body="Pick a plan to unlock the platform for your organization. You can change or cancel it at any time."
              action={
                <Button asChild>
                  <Link to="/account/subscription">
                    Choose a plan
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </Button>
              }
            />
          </Panel>
        )}

        {isOwner && (subscription || isLoading) && (
          <Panel
            title="Plan"
            action={
              <Button asChild variant="outline" size="sm">
                <Link to="/account/subscription">Manage</Link>
              </Button>
            }
            bodyClassName="px-5 py-1"
          >
            {isLoading ? (
              <div className="py-4">
                <LoadingRows rows={4} />
              </div>
            ) : (
              <dl>
                <DataRow label="Current plan" value={basePlan?.planName ?? 'None'} />
                <DataRow
                  label="Status"
                  value={
                    subscription ? (
                      <StatusPill tone={SUBSCRIPTION_TONE[subscription.status]}>
                        {SUBSCRIPTION_LABEL[subscription.status]}
                      </StatusPill>
                    ) : (
                      '—'
                    )
                  }
                />
                <DataRow
                  label="Billing cycle"
                  value={subscription?.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
                  hint={
                    subscription
                      ? formatMoney(
                          subscription.billingCycle === 'yearly'
                            ? subscription.totalYearly
                            : subscription.totalMonthly,
                          currency
                        )
                      : undefined
                  }
                />
                <DataRow
                  label={subscription?.status === 'cancelled' ? 'Access until' : 'Renews on'}
                  value={formatDay(subscription?.nextBillingDate ?? subscription?.currentPeriodEnd)}
                  mono
                />
                <DataRow
                  label="Add-ons"
                  value={
                    addons.length > 0
                      ? addons.map((addon) => addon.planName).join(', ')
                      : 'None'
                  }
                />
              </dl>
            )}
          </Panel>
        )}

        {isOwner && dashboard?.upcomingInvoice && (
          <Panel
            title="Next invoice"
            description={`Scheduled for ${formatDay(dashboard.upcomingInvoice.dueDate)}.`}
            bodyClassName="px-5 py-1"
          >
            <dl>
              {dashboard.upcomingInvoice.items.map((item, index) => (
                <DataRow
                  key={`${item.description}-${index}`}
                  label={item.description}
                  value={formatMoney(item.amount, currency)}
                  mono
                />
              ))}
              <DataRow
                label={<span className="font-medium text-foreground">Total</span>}
                value={formatMoney(dashboard.upcomingInvoice.amount, currency)}
                mono
              />
            </dl>
          </Panel>
        )}

        {isOwner && (
          <Panel
            title="Recent invoices"
            action={
              <Button asChild variant="ghost" size="sm">
                <Link to="/account/billing">
                  All invoices
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            }
            bodyClassName={recentInvoices.length ? 'px-5 py-1' : 'p-0'}
          >
            {recentInvoices.length === 0 ? (
              <EmptyState
                icon={FileText}
                title="No invoices yet"
                body="Invoices appear here once your first billing period closes."
              />
            ) : (
              <dl>
                {recentInvoices.map((invoice) => (
                  <DataRow
                    key={invoice.id}
                    label={invoice.number ?? invoice.id}
                    value={formatMinorMoney(invoice.amountDue, invoice.currency)}
                    hint={formatDay(invoice.created)}
                    mono
                    action={
                      invoice.hostedInvoiceUrl ? (
                        <a
                          href={invoice.hostedInvoiceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm font-medium text-brand-cyan hover:underline"
                        >
                          View
                        </a>
                      ) : undefined
                    }
                  />
                ))}
              </dl>
            )}
          </Panel>
        )}

        <Panel
          title="Organization"
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/account/organization">Manage</Link>
            </Button>
          }
          bodyClassName="px-5 py-1"
        >
          <dl>
            <DataRow label="Name" value={context?.organization?.name ?? '—'} />
            <DataRow label="Your role" value={<span className="capitalize">{context?.role?.name ?? '—'}</span>} />
            <DataRow label="Members" value={members?.total ?? '—'} mono />
          </dl>
        </Panel>
      </div>
    </>
  );
}
